import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import { Alert } from '@mantine/core';
import {
  CodeStepExecutionCacheMode,
  DataInput,
  ExecutionType,
  isProvidedCodeSourceMode,
  TransformCachedCodeReuseScope,
  TransformCodeSourceMode,
} from '@/types/automationFields.ts';
import type { DecisionControlLogic } from '@/types/automationSteps.ts';
import { useEditingVersionSnapshot } from '@/context/EditVersionContext/useEditVersionContext.ts';
import {
  getDecisionCodeSourceMode,
  getDecisionExecutionType,
  makeDecisionControlLogic,
} from '@/utils/decisionControlLogic.ts';
import CodeInputContractFields from '@/pages/Editor/components/StepSidebar/StepForms/CodeInputContractFields.tsx';
import { getConsumerSchemaOptions } from '@/pages/Editor/components/StepSidebar/StepForms/consumerSchemaOptions.ts';

type Props = {
  controlLogic?: Partial<DecisionControlLogic> | null;
  onChangeNoDelay: (controlLogic: DecisionControlLogic) => void;
  onChange?: (controlLogic: DecisionControlLogic) => void;
  executionTypeLabel?: string;
  invalidProvidedCode?: string;
  dataInputs?: DataInput[];
};

const CODE_SOURCE_OPTIONS = [
  { value: TransformCodeSourceMode.GenerateAlways, label: 'Generate every run' },
  { value: TransformCodeSourceMode.ReuseCachedCode, label: 'Reuse generated code' },
  { value: TransformCodeSourceMode.ProvidedCode, label: 'Use provided code' },
  {
    value: TransformCodeSourceMode.ProvidedCodeWithGeneratedFallback,
    label: 'Try provided, then generate',
  },
];

const CACHED_CODE_REUSE_SCOPE_OPTIONS = [
  { value: TransformCachedCodeReuseScope.WithinParentScope, label: 'Within this loop/run scope' },
  { value: TransformCachedCodeReuseScope.AcrossRuns, label: 'Across runs for this step' },
];

const STEP_EXECUTION_CACHE_OPTIONS = [
  { value: CodeStepExecutionCacheMode.Automatic, label: 'Automatic' },
  { value: CodeStepExecutionCacheMode.AlwaysFresh, label: 'Always run fresh' },
];

const DecisionControlLogicFields = ({
  controlLogic,
  onChangeNoDelay,
  onChange,
  executionTypeLabel = 'Processing Type',
  invalidProvidedCode,
  dataInputs = [],
}: Props) => {
  const nextControlLogic = makeDecisionControlLogic(controlLogic ?? undefined);
  const executionType = getDecisionExecutionType(nextControlLogic);
  const codeSourceMode = getDecisionCodeSourceMode(nextControlLogic);
  const handleChange = onChange ?? onChangeNoDelay;
  const editingVersion = useEditingVersionSnapshot();
  const schemaOptions = getConsumerSchemaOptions(editingVersion);
  const codeInputs = dataInputs.filter((input) => !!input.binding_name);
  const inputContracts = nextControlLogic.input_contracts ?? [];

  return (
    <>
      <StyledSelect
        label={executionTypeLabel}
        placeholder={`Select ${executionTypeLabel}`}
        data={Object.values(ExecutionType).map((type) => ({ value: type, label: type }))}
        value={executionType}
        onChange={(value) =>
          onChangeNoDelay(
            makeDecisionControlLogic({
              ...nextControlLogic,
              execution_type: value as ExecutionType,
            }),
          )
        }
        required={true}
        allowDeselect={false}
        clearable={false}
      />
      {executionType === ExecutionType.Code && (
        <>
          <StyledSelect
            label="Code Source"
            placeholder="Select Code Source"
            data={CODE_SOURCE_OPTIONS}
            value={codeSourceMode}
            onChange={(value) =>
              onChangeNoDelay(
                makeDecisionControlLogic({
                  ...nextControlLogic,
                  code_source_mode: value as TransformCodeSourceMode,
                }),
              )
            }
            required={true}
            allowDeselect={false}
            clearable={false}
          />
          <StyledSelect
            label="Completed Execution Reuse"
            placeholder="Select Completed Execution Reuse"
            data={STEP_EXECUTION_CACHE_OPTIONS}
            value={
              nextControlLogic.step_execution_cache_mode ?? CodeStepExecutionCacheMode.Automatic
            }
            onChange={(value) =>
              onChangeNoDelay(
                makeDecisionControlLogic({
                  ...nextControlLogic,
                  step_execution_cache_mode: value as CodeStepExecutionCacheMode,
                }),
              )
            }
            description="Automatic may reuse an equivalent completed decision. Choose Always run fresh when this code depends on live APIs, current time, randomness, mutable external state, or external effects."
            required={true}
            allowDeselect={false}
            clearable={false}
          />
          {codeSourceMode === TransformCodeSourceMode.ReuseCachedCode && (
            <>
              <StyledSelect
                label="Reuse Scope"
                placeholder="Select Reuse Scope"
                data={CACHED_CODE_REUSE_SCOPE_OPTIONS}
                value={
                  nextControlLogic.cached_code_reuse_scope ??
                  TransformCachedCodeReuseScope.AcrossRuns
                }
                onChange={(value) =>
                  onChangeNoDelay(
                    makeDecisionControlLogic({
                      ...nextControlLogic,
                      cached_code_reuse_scope: value as TransformCachedCodeReuseScope,
                    }),
                  )
                }
                required={true}
                allowDeselect={false}
                clearable={false}
              />
              {!!nextControlLogic.provided_code?.trim() && (
                <Alert color="yellow" title="Provided code will not run">
                  This control logic reuses accepted generated code. The retained provided code is
                  ignored at runtime. Switch Code Source to “Use provided code” to execute it, or
                  clear it intentionally.
                </Alert>
              )}
            </>
          )}
          {isProvidedCodeSourceMode(codeSourceMode) && (
            <StyledTextArea
              label="Provided Code"
              value={nextControlLogic.provided_code ?? ''}
              placeholder="def evaluate_control_logic(inputs):"
              onChange={(event) =>
                handleChange(
                  makeDecisionControlLogic({
                    ...nextControlLogic,
                    provided_code: event.currentTarget.value,
                  }),
                )
              }
              required={true}
              autosize={true}
              minRows={8}
              error={invalidProvidedCode}
            />
          )}
          {codeInputs.length > 0 && (
            <CodeInputContractFields
              bindingNames={codeInputs.flatMap((input) =>
                input.binding_name ? [input.binding_name] : [],
              )}
              contracts={inputContracts}
              schemaOptions={schemaOptions}
              onChange={(input_contracts) =>
                onChangeNoDelay(makeDecisionControlLogic({ ...nextControlLogic, input_contracts }))
              }
            />
          )}
        </>
      )}
    </>
  );
};

export default DecisionControlLogicFields;
